// frontend/src/pages/admin/AdminDelivery.js
import React, { useState, useEffect, useCallback } from 'react';
import { ordersApi } from '../../api/orders';
import { deliveryApi } from '../../api/delivery';
import { serviceOpsApi } from '../../api/serviceOps';
// Removed unused import: import helpers from '../../utils/helpers';
import { 
    FaTruck, FaBoxOpen, FaMapMarkerAlt, 
    FaShippingFast, FaCheckCircle, FaTimes, FaUserTie, FaSpinner, FaCalendarAlt, FaPaperPlane, FaInfoCircle, FaImage
    // Removed unused import: FaEye
} from 'react-icons/fa';

function AdminDelivery() {
  const [activeTab, setActiveTab] = useState('ready'); // 'ready' or 'active'
  const [orders, setOrders] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [logisticsPartners, setLogisticsPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [assignData, setAssignData] = useState({
      logistics_partner_name: '',
      tracking_number: '',
      estimated_delivery_date: '',
      notes: ''
  });
  const [processing, setProcessing] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [allOrders, allDeliveries, allProviders] = await Promise.all([
        ordersApi.getAllOrdersAdmin(),
        deliveryApi.getAllDeliveries(),
        // Fetch all active logistics partners
        serviceOpsApi.getAllProviders({ type: 'logistics_partner', is_active: true })
      ]);

      // Filter for orders that are PAID or PROCESSING but NOT yet shipped/delivered
      const readyToShip = allOrders.filter(o => 
          (o.status === 'processing' || o.status === 'paid' || o.payment_method === 'pay_on_delivery') && 
          o.status !== 'shipped' && 
          o.status !== 'delivered' &&
          o.status !== 'cancelled'
      );

      setOrders(readyToShip);
      setDeliveries(allDeliveries);
      setLogisticsPartners(allProviders);
      
      // Set default partner in assignData if available
      if (allProviders.length > 0) {
          setAssignData(prev => ({ ...prev, logistics_partner_name: allProviders[0].name }));
      }
      
    } catch (err) {
      console.error("Error loading logistics data:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- HANDLERS ---

  const handleOpenAssignModal = (order) => {
      setSelectedOrder(order);
      // Reset form data, keeping the default partner name
      setAssignData(prev => ({
          ...prev,
          tracking_number: '',
          estimated_delivery_date: '',
          notes: ''
      }));
      setIsModalOpen(true);
  };

  const handleAssignSubmit = async (e) => {
      e.preventDefault();
      setProcessing(true);
      
      if (!selectedOrder) return;
      if (!assignData.logistics_partner_name) return alert("Please select a logistics partner.");

      try {
          await deliveryApi.assignDelivery({
              order_id: selectedOrder.id,
              ...assignData
          });
          alert("Delivery Assigned Successfully! Order status updated to Shipped.");
          setIsModalOpen(false);
          fetchData(); // Refresh lists
      } catch (err) {
          alert("Failed to assign delivery.");
      } finally {
          setProcessing(false);
      }
  };

  const handleUpdateStatus = async (assignmentId, newStatus) => {
      if(!window.confirm(`Update status to ${newStatus.toUpperCase()}?`)) return;
      try {
          await deliveryApi.updateDeliveryStatus(assignmentId, newStatus);
          fetchData();
      } catch (err) {
          alert("Failed to update status.");
      }
  };

  const getStatusBadge = (status) => {
      const styles = {
          assigned: "bg-blue-100 text-blue-800",
          picked_up: "bg-yellow-100 text-yellow-800",
          in_transit: "bg-purple-100 text-purple-800",
          delivered: "bg-green-100 text-green-800",
          failed: "bg-red-100 text-red-800"
      };
      return <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${styles[status] || 'bg-gray-100'}`}>{status.replace('_', ' ')}</span>;
  };

  if (loading) return <div className="p-8 text-center"><FaSpinner className="animate-spin text-2xl text-primary" /> Loading Logistics Data...</div>;

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
          <FaTruck className="text-primary" /> Logistics & Delivery
      </h1>

      {/* TABS - Added overflow-x-auto for mobile tabs */}
      <div className="flex gap-4 mb-6 border-b border-gray-200 overflow-x-auto">
          <button 
              onClick={() => setActiveTab('ready')}
              className={`pb-2 px-4 font-medium flex items-center gap-2 whitespace-nowrap ${activeTab === 'ready' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}
          >
              <FaBoxOpen /> Ready to Ship ({orders.length})
          </button>
          <button 
              onClick={() => setActiveTab('active')}
              className={`pb-2 px-4 font-medium flex items-center gap-2 whitespace-nowrap ${activeTab === 'active' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}
          >
              <FaShippingFast /> Active Deliveries ({deliveries.filter(d => d.status !== 'delivered').length})
          </button>
      </div>

      {/* --- TAB 1: READY TO SHIP --- */}
      {activeTab === 'ready' && (
          <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
              <table className="w-full text-left border-collapse min-w-[900px]">
                  <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                      <tr>
                          <th className="p-4 w-16"><FaImage /></th> {/* <--- IMAGE COLUMN */}
                          <th className="p-4">Order #</th>
                          <th className="p-4">Customer</th>
                          <th className="p-4">Destination</th>
                          <th className="p-4">Items</th>
                          <th className="p-4 text-right">Action</th>
                      </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                      {orders.length === 0 ? (
                          <tr><td colSpan="6" className="p-8 text-center text-gray-500">No orders waiting for shipment.</td></tr>
                      ) : (
                          orders.map(order => {
                              const firstItem = order.items?.[0] || {};
                              const itemImage = firstItem.image || 'https://via.placeholder.com/50';
                              
                              return (
                                  <tr key={order.id} className="hover:bg-gray-50">
                                      <td className="p-2">
                                          <div className="w-10 h-10 bg-gray-100 rounded overflow-hidden border border-gray-200">
                                              <img src={itemImage} alt="Item" className="w-full h-full object-cover" />
                                          </div>
                                      </td>
                                      <td className="p-4 font-mono font-bold text-sm">{order.order_number}</td>
                                      <td className="p-4 text-sm">
                                          <div className="font-bold">{order.shipping_address?.full_name}</div>
                                          <div className="text-xs text-gray-500">{order.shipping_address?.phone}</div>
                                      </td>
                                      <td className="p-4 text-sm">
                                          <div className="flex items-center gap-1 text-gray-700">
                                              <FaMapMarkerAlt className="text-gray-400" />
                                              {order.shipping_address?.city}, {order.shipping_address?.state}
                                          </div>
                                      </td>
                                      <td className="p-4 text-sm text-gray-600">
                                          {order.items.length} items
                                      </td>
                                      <td className="p-4 text-right">
                                          <button 
                                              onClick={() => handleOpenAssignModal(order)}
                                              className="bg-blue-600 text-white px-4 py-2 rounded text-xs font-bold hover:bg-blue-700 flex items-center gap-2 ml-auto"
                                          >
                                              <FaTruck /> Assign Driver
                                          </button>
                                      </td>
                                  </tr>
                              );
                          })
                      )}
                  </tbody>
              </table>
          </div>
      )}

      {/* --- TAB 2: ACTIVE DELIVERIES --- */}
      {activeTab === 'active' && (
          <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
              <table className="w-full text-left border-collapse min-w-[1000px]">
                  <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                      <tr>
                          <th className="p-4 w-16"><FaImage /></th> {/* <--- IMAGE COLUMN */}
                          <th className="p-4">Order #</th>
                          <th className="p-4">Logistics Partner</th>
                          <th className="p-4">Tracking #</th>
                          <th className="p-4">Destination</th>
                          <th className="p-4">Status</th>
                          <th className="p-4 text-right">Update Status</th>
                      </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                      {deliveries.length === 0 ? (
                          <tr><td colSpan="7" className="p-8 text-center text-gray-500">No active deliveries.</td></tr>
                      ) : (
                          deliveries.filter(d => d.status !== 'delivered' && d.status !== 'failed').map(delivery => {
                              // Find the original order to get the item image
                              const originalOrder = orders.find(o => o.id === delivery.order_id);
                              const itemImage = originalOrder?.items?.[0]?.image || 'https://via.placeholder.com/50';

                              return (
                                  <tr key={delivery.id} className="hover:bg-gray-50">
                                      <td className="p-2">
                                          <div className="w-10 h-10 bg-gray-100 rounded overflow-hidden border border-gray-200">
                                              <img src={itemImage} alt="Item" className="w-full h-full object-cover" />
                                          </div>
                                      </td>
                                      <td className="p-4">
                                          <div className="font-mono font-bold text-sm">{delivery.order_number}</div>
                                          <div className="text-xs text-gray-500">{new Date(delivery.assigned_at).toLocaleDateString()}</div>
                                      </td>
                                      <td className="p-4 text-sm font-medium text-gray-800">
                                          {delivery.logistics_partner_name || 'Internal Fleet'}
                                      </td>
                                      <td className="p-4 text-sm font-mono text-gray-600">
                                          {delivery.tracking_number || 'N/A'}
                                      </td>
                                      <td className="p-4 text-sm text-gray-600">
                                          {delivery.delivery_address}
                                      </td>
                                      <td className="p-4">
                                          {getStatusBadge(delivery.status)}
                                      </td>
                                      <td className="p-4 text-right">
                                          <div className="flex justify-end gap-2">
                                              {delivery.status === 'assigned' && (
                                                  <button onClick={() => handleUpdateStatus(delivery.id, 'picked_up')} className="bg-yellow-100 text-yellow-700 p-2 rounded hover:bg-yellow-200" title="Mark Picked Up">
                                                      <FaBoxOpen />
                                                  </button>
                                              )}
                                              {delivery.status === 'picked_up' && (
                                                  <button onClick={() => handleUpdateStatus(delivery.id, 'in_transit')} className="bg-purple-100 text-purple-700 p-2 rounded hover:bg-purple-200" title="Mark In Transit">
                                                      <FaShippingFast />
                                                  </button>
                                              )}
                                              {(delivery.status === 'in_transit' || delivery.status === 'picked_up') && (
                                                  <button onClick={() => handleUpdateStatus(delivery.id, 'delivered')} className="bg-green-100 text-green-700 p-2 rounded hover:bg-green-200" title="Mark Delivered">
                                                      <FaCheckCircle />
                                                  </button>
                                              )}
                                              <button onClick={() => handleUpdateStatus(delivery.id, 'failed')} className="bg-red-100 text-red-700 p-2 rounded hover:bg-red-200" title="Mark Failed">
                                                  <FaTimes />
                                              </button>
                                          </div>
                                      </td>
                                  </tr>
                              );
                          })
                      )}
                  </tbody>
              </table>
          </div>
      )}

      {/* --- ASSIGN MODAL --- */}
      {isModalOpen && selectedOrder && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                  <div className="flex justify-between items-center mb-4 border-b pb-2">
                      <h3 className="text-lg font-bold">Assign Delivery for {selectedOrder.order_number}</h3>
                      <button onClick={() => setIsModalOpen(false)}><FaTimes /></button>
                  </div>
                  
                  <form onSubmit={handleAssignSubmit} className="space-y-4">
                      
                      <div className="bg-gray-50 p-3 rounded text-sm text-gray-700">
                          <p className="font-bold mb-1">Customer: {selectedOrder.shipping_address?.full_name}</p>
                          <p className="flex items-center gap-1 text-xs"><FaMapMarkerAlt /> {selectedOrder.shipping_address?.address_line1}, {selectedOrder.shipping_address?.city}, {selectedOrder.shipping_address?.state}</p>
                      </div>

                      <div>
                          <label className="block text-xs font-bold text-gray-600 mb-1 flex items-center gap-1">
                              <FaUserTie /> Logistics Partner
                          </label>
                          {logisticsPartners.length > 0 ? (
                              <select 
                                  required 
                                  name="logistics_partner_name"
                                  value={assignData.logistics_partner_name}
                                  onChange={(e) => setAssignData({...assignData, logistics_partner_name: e.target.value})}
                                  className="w-full p-2 border rounded bg-white"
                              >
                                  {logisticsPartners.map(p => (
                                      <option key={p.id} value={p.name}>{p.name} ({p.location_lga_id})</option>
                                  ))}
                              </select>
                          ) : (
                              <div className="p-3 bg-yellow-50 text-yellow-800 text-sm rounded">
                                  <FaInfoCircle className="inline mr-1" /> No active Logistics Partners found.
                              </div>
                          )}
                      </div>
                      
                      <div>
                          <label className="block text-xs font-bold text-gray-600 mb-1">Tracking Number (Auto-Generated)</label>
                          <input 
                              type="text" 
                              name="tracking_number"
                              value={assignData.tracking_number}
                              onChange={(e) => setAssignData({...assignData, tracking_number: e.target.value})}
                              className="w-full p-2 border rounded"
                              placeholder="Leave empty to auto-generate"
                          />
                      </div>
                      <div>
                          <label className="block text-xs font-bold text-gray-600 mb-1 flex items-center gap-1">
                              <FaCalendarAlt /> Estimated Delivery Date
                          </label>
                          <input 
                              type="date" 
                              name="estimated_delivery_date"
                              value={assignData.estimated_delivery_date}
                              onChange={(e) => setAssignData({...assignData, estimated_delivery_date: e.target.value})}
                              className="w-full p-2 border rounded"
                          />
                      </div>
                      <div>
                          <label className="block text-xs font-bold text-gray-600 mb-1">Notes</label>
                          <textarea 
                              name="notes"
                              value={assignData.notes}
                              onChange={(e) => setAssignData({...assignData, notes: e.target.value})}
                              className="w-full p-2 border rounded"
                              rows="2"
                              placeholder="Instructions for driver..."
                          ></textarea>
                      </div>

                      <div className="flex justify-end gap-2 pt-2 border-t">
                          <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-gray-600">Cancel</button>
                          <button 
                              type="submit" 
                              disabled={processing || logisticsPartners.length === 0} 
                              className={`px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2 ${logisticsPartners.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                              {processing ? <FaSpinner className="animate-spin" /> : <FaPaperPlane />} Confirm Assignment
                          </button>
                      </div>
                  </form>
              </div>
          </div>
      )}

    </div>
  );
}

export default AdminDelivery;